import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Missing reset token. Please use the link from your email.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
        silent: true,
      });
      const data = await res.clone().json().catch(() => ({}));
      if (res.ok) {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 2500);
      } else {
        setError(data.detail || 'Could not reset password.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="glass rounded-2xl p-10 glow-box text-center max-w-md" data-testid="reset-success">
          <CheckCircle2 className="mx-auto text-[#00ff00] mb-4" size={72} />
          <h2 className="text-3xl font-bold text-[#00ff00] mb-3 glow">Password updated!</h2>
          <p className="text-[#00f0ff]">Redirecting you to sign in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="grid-background absolute inset-0 opacity-20"></div>
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-[#00f0ff] via-[#ff00ff] to-[#b026ff] text-transparent bg-clip-text mb-2">
            Set a new password
          </h1>
          <p className="text-[#00f0ff]/70">Choose a strong password (at least 8 characters).</p>
        </div>
        <div className="glass rounded-2xl p-8 glow-box">
          {error && (
            <div
              className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm"
              data-testid="reset-error"
            >
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">New password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#ff00ff]/50" size={20} />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#0a0a1f]/50 border border-[#ff00ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none"
                  placeholder="At least 8 characters"
                  minLength={8}
                  required
                  data-testid="reset-new-password-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">Confirm password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#ff00ff]/50" size={20} />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#0a0a1f]/50 border border-[#ff00ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none"
                  placeholder="Re-enter new password"
                  minLength={8}
                  required
                  data-testid="reset-confirm-password-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 btn-primary text-white font-bold rounded-lg disabled:opacity-50"
              data-testid="reset-submit-button"
            >
              {loading ? 'Updating...' : 'Update password'}
            </button>
          </form>
          <p className="mt-6 text-center text-[#00f0ff]/60 text-sm">
            <Link to="/login" className="hover:text-[#00f0ff] hover:underline">Back to sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
